/* assets/src/viewer/core/viewer.js
 * Orchestratore centrale del viewer. Aggrega renderer, scena, camera, controlli e loop.
 * Espone un'API pulita (applyConfig/toggleRoof/setEnvironment/focus/renderViewsForPdf)
 * usata dagli shim window.ASG_* e dalla facade ASG_Viewer (modulo contract).
 */
import { Vector3, Box3 } from 'three';
import { clamp, pixelRatioCap } from '../settings.js';
import { createRenderer } from './renderer.js';
import { SceneManager } from './scene.js';
import { createCamera, fitBounds } from './camera.js';
import { createControls } from './controls.js';
import { RenderLoop } from './loop.js';

export class Viewer {
  constructor(container) {
    this.container = container;
    this.renderer = createRenderer(container);
    this.sceneManager = new SceneManager(this.renderer);
    this.scene = this.sceneManager.scene;
    this.camera = createCamera(container);
    this.loop = new RenderLoop(this);
    // Invalida il render quando arrivano asset async (texture terreno, ecc.).
    this.sceneManager.onNeedsRender = () => this.loop.requestRender();

    this.currentSceneBounds = null;
    this.currentModelType = null;
    this.hasActiveModel = false;
    this.autoRotateInterrupted = false;
    this.controller = null; // PergolaController (impostato dopo)
    this.gizmo = null;       // overlay assi opzionale

    this.controls = createControls(this.camera, this.renderer.domElement, {
      onChange: () => this.loop.requestViewRender(),
      onStart: () => { if (this.hasActiveModel) this.disableAutoRotate(); this.loop.requestViewRender(); },
      onEnd: () => this.loop.requestViewRender(),
      onInteract: () => { if (this.hasActiveModel) this.disableAutoRotate(); this.loop.requestViewRender(); },
    });

    this._bindResize();
    this.loop.start();
  }

  // ----- render invalidation -----
  requestRender() { this.loop.requestRender(); }
  requestViewRender() { this.loop.requestViewRender(); }

  // ----- animazioni per-frame (telo/copertura + fly-to camera) -----
  stepAnimations() {
    let animated = false;
    if (typeof window.ASG_TeloTarget !== 'undefined') {
      if (typeof window.ASG_TeloProgress === 'undefined') window.ASG_TeloProgress = window.ASG_TeloTarget;
      if (Math.abs(window.ASG_TeloProgress - window.ASG_TeloTarget) > 0.001) {
        const fg = window.activeFabricGroup;
        const speed = fg && fg.userData ? Number(fg.userData.animationSpeed || 0.015) : 0.015;
        const minSpeed = fg && fg.userData ? Number(fg.userData.minAnimationSpeed || 0.002) : 0.002;
        window.ASG_TeloProgress += (window.ASG_TeloTarget - window.ASG_TeloProgress) * clamp(speed, minSpeed, 0.03);
        if (fg && fg.userData.updateLayout) fg.userData.updateLayout(window.ASG_TeloProgress);
        animated = true;
      }
    }
    if (this._advanceCamTween(performance.now())) animated = true;
    return animated;
  }

  // ----- fly-to morbido + focus per categoria -----
  flyTo(target, position, duration = 480) {
    this._camTween = {
      t0: performance.now(), duration,
      fromT: this.controls.target.clone(), toT: target.clone(),
      fromP: this.camera.position.clone(), toP: position.clone(),
    };
    this.requestViewRender();
  }

  _advanceCamTween(now) {
    if (!this._camTween) return false;
    const tw = this._camTween;
    const t = clamp((now - tw.t0) / tw.duration, 0, 1);
    const e = 1 - Math.pow(1 - t, 3);
    this.controls.target.lerpVectors(tw.fromT, tw.toT, e);
    this.camera.position.lerpVectors(tw.fromP, tw.toP, e);
    this.controls.update();
    if (t >= 1) { this._camTween = null; return false; }
    return true;
  }

  focus(category) {
    this.disableAutoRotate();
    const box = this.currentSceneBounds || (this.controller && this.controller.model
      ? new Box3().setFromObject(this.controller.model.group) : null);
    if (!box || box.isEmpty()) return;
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());
    const target = center.clone();
    let distFactor = 1.0;
    const cat = String(category || '').toLowerCase();
    if (cat === 'finishes' || cat === 'colore' || cat === 'colori' || cat === 'appearance') {
      target.y = box.max.y - size.y * 0.18; distFactor = 0.82;
    } else if (cat === 'dimensions' || cat === 'misure') {
      distFactor = 1.12;
    }
    const dir = this.camera.position.clone().sub(this.controls.target);
    const curDist = Math.max(0.001, dir.length());
    dir.normalize();
    const newPos = target.clone().add(dir.multiplyScalar(curDist * distFactor));
    this.flyTo(target, newPos, 460);
  }

  // ----- camera / framing -----
  fit(box, { preserveView = false } = {}) {
    const cloned = fitBounds(this.camera, this.controls, box, { preserveView, modelType: this.currentModelType });
    if (cloned) {
      this.currentSceneBounds = cloned;
      this.sceneManager.fitContactShadow(cloned);
    }
    this.requestRender();
  }

  frameCurrentScene() {
    if (this.currentSceneBounds) this.fit(this.currentSceneBounds, { preserveView: false });
  }

  // ----- auto-rotate -----
  disableAutoRotate() {
    this.autoRotateInterrupted = true;
    if (this.controls) this.controls.autoRotate = false;
    this.requestViewRender();
  }

  enableAutoRotate(force = true) {
    this.autoRotateInterrupted = !force;
    if (this.controls) { this.controls.autoRotate = !!force; this.controls.update(); }
    this.requestViewRender();
  }

  // ----- sun / mode / ambiente -----
  updateSun(settings) { this.sceneManager.lighting.updateSun(settings); this.requestRender(); }
  setMode(mode) { this.sceneManager.lighting.setMode(mode); this.requestRender(); }
  setEnvironmentPreset(preset) {
    if (!this.furniture) return;
    this.furniture.setPreset(preset);
    this.furniture.setVisible(true);
  }

  // ----- resize -----
  _bindResize() {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; this.onResize(); });
    };
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(schedule);
      this._resizeObserver.observe(this.container);
    }
    window.addEventListener('resize', schedule, false);
  }

  onResize() {
    if (!this.camera || !this.renderer) return;
    const bounds = this.container.getBoundingClientRect();
    const width = Math.max(bounds.width || this.container.clientWidth || 1, 1);
    const height = Math.max(bounds.height || this.container.clientHeight || 1, 1);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    // pixelRatioCap importato lazy per evitare cicli; usiamo renderer corrente
    import('../settings.js').then(({ pixelRatioCap }) => {
      this.renderer.setPixelRatio(pixelRatioCap());
      this.renderer.setSize(width, height, false);
      if (this.currentSceneBounds) this.frameCurrentScene();
      this.requestRender();
    });
  }

  // ----- API alto livello (delega al controller, impostato in index) -----
  applyConfig(config) { if (this.controller) this.controller.applyConfig(config || {}); }
  clearScene() { if (this.controller) this.controller.clear(); }

  dispose() {
    this.loop.stop();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }
}
